import { save, saveChanged } from '../../lib/save';
import { metaOf } from '../../lib/tasks';

/*
  THE REVIEW HUB'S SEND BACK, TRANSFER AND DENY, 18 September 2026.

  Moved here out of AdminDashboard.jsx in the audit after the rebrand, when
  all three were found broken (issues 3, 4 and 5), so that a test can call them
  with a stand-in database and see exactly what they write. The page hands
  them what they need as `ctx`: the database client, the pop-up messages, the
  notice and audit helpers, the signed-in person, and the page's own setters.
*/

/*
  **Sending a count back**, from the Review Hub's Counts queue. Rewritten 18
  September 2026, in the audit after the rebrand (issue 3).

  **It is handed the SHEET, and it used to treat it as a task.** This was
  written when the queue was a list of "Count to approve" tasks; when the
  Review Hub became a page on 5 September it began passing the sheet itself,
  so `todo.count_id` came out empty, the status change aimed at nothing, and
  the admin was told "Count rejected. The rep has been notified." while the
  sheet stayed Submitted and locked. The rep was still sent a "Resubmit" task
  for a sheet they could not open.

  Now the sheet goes back to In progress, and that write has to change the
  sheet (`saveChanged`) before anything else happens or anybody is told. Its
  open "Count to approve" task is closed, because the count no longer waits
  for approval.
*/
export async function rejectCount(ctx, sheet, reason, countMeta) {
  const { supabase, toast, notify, logAudit, profile, setProgress, setTodos } = ctx;
  const countId = sheet?.id;
  const repId = countMeta?.repId || sheet?.rep_id || null;
  const warehouseId = sheet?.warehouse?.id || sheet?.warehouse_id || null;
  const locationName = countMeta?.locationName || sheet?.warehouse?.name || 'a location';
  if (!countId) { toast.error('Could not send the count back: this row has no sheet on it.'); return; }
  if (!await saveChanged(toast, () => supabase.from('inventory_counts')
    .update({ status: 'in_progress' }).eq('id', countId).eq('status', 'submitted').select('id'),
    'sending the count back')) return;
  await save(toast, () => supabase.from('todos')
    .update({ is_complete: true, completed_at: new Date().toISOString() })
    .eq('count_id', countId).eq('todo_type', 'count_approval').eq('is_complete', false),
    'the finished approval task');
  if (repId) {
    // Alert -- shows as a notification on the rep's dashboard
    await notify(toast, {
      alert_type: 'count_rejected',
      title: 'Count sent back for corrections',
      message: 'Your count for ' + locationName + ' was sent back and needs corrections.' + (reason ? ' Reason: ' + reason : ''),
      is_read: false,
      rep_id: repId,
      inventory_count_id: countId,
    });
    // Rep-facing todo task so they must acknowledge and resubmit
    await save(toast, () => supabase.from('todos').insert({
      title: 'Resubmit count: ' + locationName,
      description: 'Your count was sent back.' + (reason ? ' Reason: ' + reason : '') + ' Please make corrections and resubmit.',
      priority: 'high',
      todo_type: 'resubmit_required',
      warehouse_id: warehouseId,
      count_id: countId,
      rep_id: repId,
      is_complete: false,
    }), 'the rep\'s resubmit task');
  }
  setProgress(prev => prev.map(p => p.id === countId ? { ...p, status: 'in_progress' } : p));
  setTodos(prev => prev.filter(t => !(t.count_id === countId && t.todo_type === 'count_approval')));
  await logAudit(profile, 'COUNT_REJECTED', 'count',
    { target_type: 'count', target_name: locationName,
      details: { count_id: countId, reason: reason || null } });
  toast.info(repId ? 'Count sent back. The rep has been told why.' : 'Count sent back.');
}


/*
  **Handing a sheet to the rep who asked for it**, and **saying no**. Both
  rewritten 18 September 2026, in the audit after the rebrand (issues 4 and 5).

  The Review Hub calls these with the request's task, and they used to expect
  a second argument nobody passed: Transfer read `meta.requesting_rep_id` off
  nothing and stopped dead without a word, and Deny took the admin's reason
  as that argument, so the rep who asked was never told, or why, while the
  request vanished from the Hub. The request's details are read off the task
  itself now, whatever the caller passes.

  **The previous holder is the sheet's holder before the change**, read from
  the sheet. It used to be found by looking a person up by FULL NAME, which
  two people can share.
*/
export async function approveOwnershipTransfer(ctx, todo, given) {
  const { supabase, toast, notify, setTodos, loadData } = ctx;
  const meta = (given && given.requesting_rep_id) ? given : metaOf(todo);
  const countId           = todo.count_id;
  const requestingRepId   = meta.requesting_rep_id;
  const requestingRepName = meta.requesting_rep_name || 'The rep';
  const locationName      = meta.location_name       || 'the location';
  if (!countId || !requestingRepId) { toast.error('Could not transfer: the request does not say which sheet or which rep.'); return; }

  const { data: before } = await supabase.from('inventory_counts').select('rep_id').eq('id', countId).maybeSingle();
  const previousHolder = before?.rep_id || null;

  if (!await saveChanged(toast, () => supabase.from('inventory_counts')
    .update({ rep_id: requestingRepId }).eq('id', countId).select('id'),
    'the transfer')) return;
  await save(toast, () => supabase.from('todos')
    .update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todo.id),
    'the finished request');

  await notify(toast, {
    alert_type:          'transfer_approved',
    title:               'Ownership Transfer Approved',
    message:             'You have been granted ownership of ' + locationName + '. You can now enter the count.',
    is_read:             false,
    rep_id:              requestingRepId,
    inventory_count_id:  countId,
  });
  if (previousHolder && previousHolder !== requestingRepId) {
    await notify(toast, {
      alert_type:         'transfer_removed',
      title:              'Count Ownership Transferred',
      message:            'Your ownership of ' + locationName + ' has been transferred to ' + requestingRepName + ' by admin.',
      is_read:            false,
      rep_id:             previousHolder,
      inventory_count_id: countId,
    });
  }

  setTodos(prev => prev.filter(t => t.id !== todo.id));
  toast.success('Ownership transferred to ' + requestingRepName + '.');
  loadData();
}

export async function denyOwnershipTransfer(ctx, todo, given, maybeReason) {
  const { supabase, toast, notify, setTodos } = ctx;
  // Called as (todo, reason) by the Review Hub and (todo, meta, reason) by
  // older callers: take whichever was meant.
  const reason = typeof given === 'string' ? given : maybeReason;
  const meta = (given && typeof given === 'object' && given.requesting_rep_id) ? given : metaOf(todo);
  const requestingRepId = meta.requesting_rep_id || todo.rep_id || null;
  const locationName    = meta.location_name || 'the location';

  if (!await saveChanged(toast, () => supabase.from('todos')
    .update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todo.id).select('id'),
    'the answer to the request')) return;

  if (requestingRepId) {
    await notify(toast, {
      alert_type:         'transfer_denied',
      title:              'Ownership Transfer Denied',
      message:            'Your request for ownership of ' + locationName + ' was denied.' + (reason ? ' Reason: ' + reason : ''),
      is_read:            false,
      rep_id:             requestingRepId,
      inventory_count_id: todo.count_id,
    });
  }

  setTodos(prev => prev.filter(t => t.id !== todo.id));
  toast.info(requestingRepId ? 'Transfer request denied. The rep has been told why.' : 'Transfer request denied.');
}

/*
  **Approving records who approved it, and closes its task**, the owner's
  answer of 18 September 2026 (the audit after the rebrand, question 7).
  `approved_by` was written by nothing anywhere, and the "Count to approve"
  task raised when a sheet is sent in was never closed, so they piled up.
*/
export async function approveCount(ctx, countId) {
  const { supabase, toast, notify, logAudit, profile, setProgress, setTodos } = ctx;
  const { data: countData } = await supabase.from('inventory_counts').select('rep_id, warehouse:warehouses(name)').eq('id', countId).single();
  if (!await saveChanged(toast, () => supabase.from('inventory_counts')
    .update({ status: 'approved', approved_at: new Date().toISOString(), approved_by: profile?.id || null })
    .eq('id', countId).eq('status', 'submitted').select('id'),
    'the approval')) return;
  await save(toast, () => supabase.from('todos')
    .update({ is_complete: true, completed_at: new Date().toISOString() })
    .eq('count_id', countId).eq('todo_type', 'count_approval').eq('is_complete', false),
    'the finished approval task');
  setTodos(prev => prev.filter(t => !(t.count_id === countId && t.todo_type === 'count_approval')));
  setProgress(prev => prev.map(p => p.id === countId ? { ...p, status: 'approved' } : p));
  if (countData?.rep_id) {
    await notify(toast, { alert_type: 'count_approved', title: 'Count approved', message: 'Your count for ' + (countData.warehouse?.name || '') + ' has been approved.', is_read: false, rep_id: countData.rep_id, inventory_count_id: countId });
  }
  /*
    **The audit log recorded nothing at all about counting**, which is the one
    question an audit log exists for: who approved this count, and when. The
    screen has carried the words for these events since it was written and had
    a "Count Activity" filter that could never return a row.
  */
  await logAudit(profile, 'COUNT_APPROVED', 'count',
    { target_type: 'count', target_name: countData?.warehouse?.name || 'a location',
      details: { count_id: countId } });
  toast.success('Count approved.');
}
